import React, { useState } from 'react';
import { 
  ArrowLeft, 
  CreditCard, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Search, 
  Filter,
  Eye,
  DollarSign,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Activity,
  Calendar,
  User,
  Building,
  FileText
} from 'lucide-react';
import { paymentRecords, paymentProcessingDashboard, accountsPayableSummary, journalEntries } from '../../data/invoiceData';

const PaymentProcessing = ({ onBack, navigationSource }) => {
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const renderWorkflowBanner = () => {
    if (navigationSource === 'purchase-workflow') {
      return (
        <div className="bg-gradient-to-r from-orange-50 to-red-50 border-l-4 border-orange-500 p-4 mb-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-orange-500 p-2 rounded-full">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-orange-800">來自業務流程: 採購週期管理</h3>
                <p className="text-sm text-orange-600">發票核對與付款處理階段</p>
              </div>
            </div>
            <button
              onClick={onBack}
              className="flex items-center space-x-2 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>返回採購管理</span>
            </button>
          </div>
        </div>
      );
    }
    return null;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'cancelled': return 'text-red-600 bg-red-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'scheduled': return 'text-blue-600 bg-blue-100';
      case 'processing': return 'text-purple-600 bg-purple-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'cancelled': return <XCircle className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'scheduled': return <Calendar className="w-4 h-4" />;
      case 'processing': return <Activity className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'bank_transfer': return <Building className="w-4 h-4" />;
      case 'check': return <FileText className="w-4 h-4" />;
      case 'wire_transfer': return <CreditCard className="w-4 h-4" />;
      case 'cash': return <DollarSign className="w-4 h-4" />;
      default: return <CreditCard className="w-4 h-4" />;
    }
  };

  const filteredPayments = paymentRecords.filter(payment => {
    const matchesSearch = payment.supplier_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.payment_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.invoice_number.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || payment.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">付款處理管理</h1>
          <p className="text-gray-600 mt-1">管理供應商付款和會計記帳</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setActiveView('list')}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Eye className="w-4 h-4" />
            <span>付款清單</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">待處理付款</p>
              <p className="text-2xl font-bold text-gray-900">{paymentProcessingDashboard.summary.pending_payments}</p>
            </div>
            <div className="bg-yellow-100 p-3 rounded-full">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <AlertTriangle className="w-4 h-4 text-yellow-500 mr-1" />
            <span className="text-sm text-yellow-600">需要處理</span>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">已排程付款</p>
              <p className="text-2xl font-bold text-gray-900">{paymentProcessingDashboard.summary.scheduled_payments}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <TrendingUp className="w-4 h-4 text-blue-500 mr-1" />
            <span className="text-sm text-blue-600">本週排程</span>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">付款總額</p>
              <p className="text-2xl font-bold text-gray-900">
                ${paymentProcessingDashboard.summary.total_payment_amount.toLocaleString()}
              </p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-sm text-green-600">本月累計</span>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">付款準確率</p>
              <p className="text-2xl font-bold text-gray-900">{paymentProcessingDashboard.summary.payment_accuracy}%</p>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <CheckCircle className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <TrendingUp className="w-4 h-4 text-purple-500 mr-1" />
            <span className="text-sm text-purple-600">品質指標</span>
          </div>
        </div>
      </div>

      {/* Upcoming Payments */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">即將到期付款</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  發票編號
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  供應商
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  金額
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  到期日
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  付款方式
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  優先級
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paymentProcessingDashboard.upcoming_payments.map((payment) => (
                <tr key={payment.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{payment.invoice_number}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{payment.supplier_name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">${payment.amount.toLocaleString()}</div>
                    {payment.early_discount_available && (
                      <div className="text-sm text-green-600">
                        早付折扣: ${payment.discount_amount.toLocaleString()}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{payment.due_date}</div>
                    {payment.discount_deadline && (
                      <div className="text-sm text-green-600">
                        折扣截止: {payment.discount_deadline}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {getPaymentMethodIcon(payment.payment_method)}
                      <span className="text-sm text-gray-900">
                        {payment.payment_method === 'bank_transfer' && '銀行轉帳'}
                        {payment.payment_method === 'check' && '支票'}
                        {payment.payment_method === 'wire_transfer' && '電匯'}
                        {payment.payment_method === 'cash' && '現金'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      payment.priority === 'high' ? 'bg-red-100 text-red-800' :
                      payment.priority === 'normal' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {payment.priority === 'high' && '高'}
                      {payment.priority === 'normal' && '一般'}
                      {payment.priority === 'low' && '低'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button className="text-blue-600 hover:text-blue-900">
                      處理付款
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payment Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">付款趨勢</h3>
          <div className="space-y-4">
            {paymentProcessingDashboard.payment_trends.monthly_payments.map((trend, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{trend.month}</span>
                <div className="flex items-center space-x-2">
                  <div className="text-sm text-gray-500">{trend.count}筆</div>
                  <div className="w-24 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ width: `${(trend.amount / 1500000) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium text-gray-900">
                    ${(trend.amount / 1000).toFixed(0)}K
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">付款方式分析</h3>
          <div className="space-y-4">
            {paymentProcessingDashboard.payment_trends.payment_methods.map((method, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  {getPaymentMethodIcon(method.method)}
                  <span className="text-sm text-gray-600">
                    {method.method === 'bank_transfer' && '銀行轉帳'}
                    {method.method === 'check' && '支票'}
                    {method.method === 'wire_transfer' && '電匯'}
                    {method.method === 'cash' && '現金'}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="text-sm text-gray-500">{method.count}筆</div>
                  <div className="w-24 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full" 
                      style={{ width: `${method.percentage}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium text-gray-900">{method.percentage}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Approval Workflow */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">待核准付款</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  發票編號
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  供應商
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  金額
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  申請人
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  當前核准人
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  核准進度
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paymentProcessingDashboard.approval_workflow.map((approval) => (
                <tr key={approval.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{approval.invoice_number}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{approval.supplier_name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">${approval.amount.toLocaleString()}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{approval.submitted_by}</div>
                    <div className="text-sm text-gray-500">
                      {new Date(approval.submitted_at).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{approval.current_approver}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <div className="w-12 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${(approval.approval_level / approval.total_levels) * 100}%` }}
                        ></div>
                      </div>
                      <span className="text-sm text-gray-600">
                        {approval.approval_level}/{approval.total_levels}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button className="text-blue-600 hover:text-blue-900">
                      查看詳情
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderPaymentList = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">付款清單</h1>
          <p className="text-gray-600 mt-1">所有付款記錄</p>
        </div>
        <button
          onClick={() => setActiveView('dashboard')}
          className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>返回儀表板</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="搜尋付款編號、發票或供應商..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">所有狀態</option>
                <option value="pending">待處理</option>
                <option value="scheduled">已排程</option>
                <option value="processing">處理中</option>
                <option value="completed">已完成</option>
                <option value="cancelled">已取消</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Payment List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  付款資訊
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  供應商
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  金額
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  付款方式
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  日期
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  狀態
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPayments.map((payment) => (
                <tr key={payment.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{payment.payment_number}</div>
                    <div className="text-sm text-gray-500">發票: {payment.invoice_number}</div>
                    <div className="text-sm text-gray-500">參考: {payment.transaction_details.reference_number}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{payment.supplier_name}</div>
                    <div className="text-sm text-gray-500">{payment.bank_details.account_holder}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">${payment.payment_amount.toLocaleString()}</div>
                    <div className="text-sm text-gray-500">{payment.currency}</div>
                    {payment.transaction_details.processing_fee > 0 && (
                      <div className="text-sm text-gray-500">
                        手續費: ${payment.transaction_details.processing_fee}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {getPaymentMethodIcon(payment.payment_method)}
                      <span className="text-sm text-gray-900">
                        {payment.payment_method === 'bank_transfer' && '銀行轉帳'}
                        {payment.payment_method === 'check' && '支票'}
                        {payment.payment_method === 'wire_transfer' && '電匯'}
                        {payment.payment_method === 'cash' && '現金'}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500">{payment.bank_details.bank_name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {new Date(payment.payment_date).toLocaleDateString()}
                    </div>
                    <div className="text-sm text-gray-500">
                      處理: {new Date(payment.processed_at).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(payment.status)}`}>
                      {getStatusIcon(payment.status)}
                      <span>
                        {payment.status === 'completed' && '已完成'}
                        {payment.status === 'cancelled' && '已取消'}
                        {payment.status === 'pending' && '待處理'}
                        {payment.status === 'scheduled' && '已排程'}
                        {payment.status === 'processing' && '處理中'}
                      </span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => {
                        setSelectedPayment(payment.id);
                        setActiveView('detail');
                      }}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      查看詳情
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderPaymentDetail = () => {
    const payment = paymentRecords.find(p => p.id === selectedPayment);
    if (!payment) return null;

    const journalEntry = journalEntries.find(j => j.payment_id === payment.id);

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">付款詳情</h1>
            <p className="text-gray-600 mt-1">{payment.payment_number}</p>
          </div>
          <button
            onClick={() => setActiveView('list')}
            className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>返回清單</span>
          </button>
        </div>

        {/* Payment Info */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">付款資訊</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">付款編號:</span>
                <span className="text-gray-900 font-medium">{payment.payment_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">發票編號:</span>
                <span className="text-gray-900 font-medium">{payment.invoice_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">供應商:</span>
                <span className="text-gray-900 font-medium">{payment.supplier_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">付款日期:</span>
                <span className="text-gray-900 font-medium">
                  {new Date(payment.payment_date).toLocaleDateString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">付款方式:</span>
                <div className="flex items-center space-x-2">
                  {getPaymentMethodIcon(payment.payment_method)}
                  <span className="text-gray-900 font-medium">
                    {payment.payment_method === 'bank_transfer' && '銀行轉帳'}
                    {payment.payment_method === 'check' && '支票'}
                    {payment.payment_method === 'wire_transfer' && '電匯'}
                    {payment.payment_method === 'cash' && '現金'}
                  </span>
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">處理人員:</span>
                <span className="text-gray-900 font-medium">{payment.processed_by}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">核准人員:</span>
                <span className="text-gray-900 font-medium">{payment.approved_by}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">金額詳情</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">付款金額:</span>
                <span className="text-gray-900 font-medium">${payment.payment_amount.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">幣別:</span>
                <span className="text-gray-900 font-medium">{payment.currency}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">手續費:</span>
                <span className="text-gray-900 font-medium">${payment.transaction_details.processing_fee}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">匯率:</span>
                <span className="text-gray-900 font-medium">{payment.transaction_details.exchange_rate}</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="text-gray-900 font-semibold">實付金額:</span>
                <span className="text-gray-900 font-semibold text-lg">${payment.transaction_details.net_amount.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">狀態:</span>
                <span className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(payment.status)}`}>
                  {getStatusIcon(payment.status)}
                  <span>
                    {payment.status === 'completed' && '已完成'}
                    {payment.status === 'cancelled' && '已取消'}
                    {payment.status === 'pending' && '待處理'}
                    {payment.status === 'scheduled' && '已排程'}
                    {payment.status === 'processing' && '處理中'}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bank Details */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">銀行資訊</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">銀行名稱:</span>
                <span className="text-gray-900 font-medium">{payment.bank_details.bank_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">帳戶號碼:</span>
                <span className="text-gray-900 font-medium">{payment.bank_details.account_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">SWIFT代碼:</span>
                <span className="text-gray-900 font-medium">{payment.bank_details.swift_code}</span>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">戶名:</span>
                <span className="text-gray-900 font-medium">{payment.bank_details.account_holder}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">參考編號:</span>
                <span className="text-gray-900 font-medium">{payment.transaction_details.reference_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">交易編號:</span>
                <span className="text-gray-900 font-medium">{payment.transaction_details.transaction_id}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Journal Entry */}
        {journalEntry && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">會計分錄</h3>
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-600">分錄編號:</span>
                <span className="text-gray-900 font-medium">{journalEntry.entry_number}</span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-600">分錄日期:</span>
                <span className="text-gray-900 font-medium">
                  {new Date(journalEntry.entry_date).toLocaleDateString()}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">摘要:</span>
                <span className="text-gray-900 font-medium">{journalEntry.description}</span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      科目代碼
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      科目名稱
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      借方
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      貸方
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      摘要
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {journalEntry.entries.map((entry, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {entry.account_code}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {entry.account_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {entry.debit > 0 ? `$${entry.debit.toLocaleString()}` : ''}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {entry.credit > 0 ? `$${entry.credit.toLocaleString()}` : ''}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {entry.description}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50">
                  <tr>
                    <td colSpan="2" className="px-6 py-3 text-sm font-medium text-gray-900">
                      總計
                    </td>
                    <td className="px-6 py-3 text-sm font-medium text-gray-900">
                      ${journalEntry.total_debit.toLocaleString()}
                    </td>
                    <td className="px-6 py-3 text-sm font-medium text-gray-900">
                      ${journalEntry.total_credit.toLocaleString()}
                    </td>
                    <td className="px-6 py-3"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* Notes */}
        {payment.notes && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">備註</h3>
            <p className="text-gray-700">{payment.notes}</p>
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    switch (activeView) {
      case 'dashboard':
        return renderDashboard();
      case 'list':
        return renderPaymentList();
      case 'detail':
        return renderPaymentDetail();
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="p-6">
      {renderWorkflowBanner()}
      {renderContent()}
    </div>
  );
};

export default PaymentProcessing; 